// The TON-equivalent quote set: native TON and the two pTON masters — the
// same set as the backend's dex.IsTONEquivalent / dex.TONQuoteSQLList.
import type { TradeSummary } from '../api/types'

const TON_QUOTE_ASSETS = new Set([
  'native',
  'EQCM3B12QK1e4yZSf8GtBRT0aLMNyEsBc_DhVfRRtOEffLez',
  'EQBnGWMCf3-FZZq1W4IWcWiGAc3PHuZ0_H-7sad2oY00o83S',
])

export function isTonQuoteToken(asset: string | undefined): boolean {
  return asset !== undefined && TON_QUOTE_ASSETS.has(asset)
}

/** A quote token that is not TON-equivalent. Empty/absent reads as TON — the
 *  same rule the backend's pool-row predicate uses. */
export function isNonTonQuote(quoteToken: string | null | undefined): boolean {
  return Boolean(quoteToken) && !isTonQuoteToken(quoteToken as string)
}

// tradeRowUsdRate is the quote→USD rate of ONE trade row — the single order
// every reader of a row's USD shares (feed table, time windows, Discover
// counters, trade-panel carry):
//   1. ton_usd_rate — the row's own stamp, written by the indexer with the
//      trade (TON/USD for a TON quote, 1 for USD₮, the priced jetton rate);
//   2. mcap_quote_usd — the rate its valuation envelope was priced with;
//   3. price_usd / price — legacy recovery for a TON-quoted row that carries
//      neither. It is a rate only when both prices share one basis (durable
//      rows); tape and pending rows carry the raw fill against the candle
//      basis, off by the fee — which is why it comes last.
// A row quoted outside TON has no third step: without its own rate it is
// unpriced (null) — never TON/USD, never a ratio. There is no pending guard
// here: a reader that sums USD and must count a pending frame once guards it.
export function tradeRowUsdRate(row: {
  price?: number | null
  price_usd?: number | null
  mcap_quote_usd?: number | null
  ton_usd_rate?: number | null
  quote_token?: string | null
}): number | null {
  if (finitePositive(row.ton_usd_rate)) return row.ton_usd_rate
  if (finitePositive(row.mcap_quote_usd)) return row.mcap_quote_usd
  if (isNonTonQuote(row.quote_token)) return null
  if (!finitePositive(row.price) || !finitePositive(row.price_usd)) return null
  const rate = row.price_usd / row.price
  return finitePositive(rate) ? rate : null
}

// tradeUsdPrice is the trade's USD price the way the indexer stamps it:
// EXECUTION price × the trade's own quote→USD rate. A stamped price_usd —
// TON, USD₮, anything the durable valuation priced — wins unchanged. A
// non-TON-quoted trade carries no price_usd (the durable valuation refuses
// non-USD₮ jetton quotes): its stamped ton_usd_rate is the only honest rate —
// 0/absent stays null, never TON/USD, never a row-level rate. A pending frame
// is unvalued for every quote: the valued patch with the same identity prices
// it once.
export function tradeUsdPrice(trade: TradeSummary): number | null {
  if (finitePositive(trade.price_usd)) return trade.price_usd
  if (trade.mc_status === 'pending') return null
  if (!isNonTonQuote(trade.quote_token)) return null
  if (!finitePositive(trade.ton_usd_rate) || !finitePositive(trade.price)) return null
  return trade.price * trade.ton_usd_rate
}

// scaledValuation mirrors the backend's scaledPairValuation: a non-TON-quoted
// trade carries no mcap/fdv envelope, so the row's USD valuation rides the
// live USD price move — previous × derived / previous USD price. Every leg
// must be honest: a non-TON quote, a real derived price, a finite positive
// previous USD price and a finite previous valuation. TON trades return null —
// an absent valuation there means unpriced, never something to scale.
export function scaledValuation(
  trade: TradeSummary,
  usdPrice: number | null,
  prevPrice: number | null | undefined,
  prevValuation: number | null | undefined
): number | null {
  if (!isNonTonQuote(trade.quote_token)) return null
  if (usdPrice == null || !Number.isFinite(usdPrice) || usdPrice <= 0) return null
  if (!finitePositive(prevPrice)) return null
  if (typeof prevValuation !== 'number' || !Number.isFinite(prevValuation)) return null
  return prevValuation * (usdPrice / prevPrice)
}

function finitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}
